import React, { useEffect, useState } from "react";
import { Form, Button, Table, Modal, Spinner } from "react-bootstrap";
import { getCourses } from "../services/courseService";
import { getSkills } from "../services/skillService";
import {
  getLessons,
  createLesson,
  deleteLesson,
  updateLesson,
} from "../services/lessonService";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { PencilIcon, TrashIcon } from "lucide-react";

const Lessons = () => {
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    course: null,
    skills: [],
  });
  const [editingLessonId, setEditingLessonId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lessonList, courseList, skillList] = await Promise.all([
        getLessons(),
        getCourses(),
        getSkills(),
      ]);
      setLessons(lessonList);
      setCourses(courseList);
      setSkills(skillList);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleEdit = (id) => {
    setEditingLessonId(id);
    const lesson = lessons.find((l) => l.id === id);
    if (lesson) {
      setFormData({
        name: lesson.name,
        description: lesson.description,
        course: lesson.course,
        skills: lesson.skills || [],
      });
      setShowModal(true);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.course) {
      toast.error("Please fill required fields");
      return;
    }
    setBtnLoading(true);
    if (editingLessonId) {
      await updateLesson(editingLessonId, formData);
      toast.success("Lesson updated successfully");
      setLessons(
        lessons.map((lesson) =>
          lesson.id === editingLessonId ? { ...lesson, ...formData } : lesson
        )
      );
      setEditingLessonId(null);
    } else {
      const newLesson = await createLesson(formData);
      toast.success("Lesson created successfully");
      setLessons([...lessons, newLesson]);
    }
    setShowModal(false);
    setFormData({ name: "", description: "", course: null, skills: [] });
    setBtnLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);
    await deleteLesson(id);
    toast.success("Lesson deleted");
    setLessons(lessons.filter((lesson) => lesson.id !== id));
    setLoading(false);
  };

  const filteredLessons = lessons
    .filter((s) =>
      Object.values(s).some((value) =>
        String(value).toLowerCase().includes(filter.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aValue = String(a[sortKey]).toLowerCase();
      const bValue = String(b[sortKey]).toLowerCase();
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredLessons.length / pageSize);
  const paginatedLessons = filteredLessons.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-semibold m-0 p-0">
          Lessons ({lessons.length})
        </h2>
        <Button onClick={() => setShowModal(true)}>Add Lesson</Button>
      </div>

      {loading && <Loader />}

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Description</th>
            <th>Course</th>
            <th>Skills</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedLessons.length === 0 && !loading && (
            <tr>
              <td colSpan="7" className="text-center">
                No lessons found
              </td>
            </tr>
          )}
          {paginatedLessons.map((lesson, idx) => (
            <tr key={lesson.id}>
              <td>{idx + 1}</td>
              <td>{lesson.name}</td>
              <td>{lesson.description}</td>
              <td>{lesson.course?.name}</td>
              <td>{lesson.skills?.map((s) => s.name).join(", ")}</td>
              <td>{new Date(lesson.createdAt).toLocaleDateString()}</td>
              <td>
                <div className="flex gap-2">
                  <Button variant="info" onClick={() => handleEdit(lesson.id)}>
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(lesson.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Add Lesson Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Lesson</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter lesson name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter lesson description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Course *</Form.Label>
              <Form.Select
                value={formData.course?.id || ""}
                onChange={(e) => {
                  const selectedCourse = courses.find(
                    (c) => c.id === e.target.value
                  );
                  setFormData({
                    ...formData,
                    course: selectedCourse
                      ? { id: selectedCourse.id, name: selectedCourse.name }
                      : null,
                  });
                }}
              >
                <option value="">Select Course</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Skills</Form.Label>
              <div className="grid grid-cols-3 gap-2">
                {skills.map((skill) => (
                  <Form.Check
                    key={skill.id}
                    type="checkbox"
                    label={skill.name}
                    checked={formData.skills.some((s) => s.id === skill.id)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        skills: e.target.checked
                          ? prev.skills.some((s) => s.id === skill.id)
                            ? prev.skills
                            : [...prev.skills, skill]
                          : prev.skills.filter((s) => s.id !== skill.id),
                      }))
                    }
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={btnLoading}>
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">
                  {editingLessonId ? "Updating..." : "Creating..."}
                </span>
              </>
            ) : (
              <>{editingLessonId ? "Update" : "Create"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Lessons;
